<template>
  <div class="min-h-screen bg-slate-50 py-10">
    <Head :title="`Cuota Condominio: ${formatDate(order.date)}`" />

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Top Actions Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 pb-4 border-b border-slate-200">
        <div class="flex items-center space-x-3 mb-4 sm:mb-0">
          <button 
            @click="back()"
            class="inline-flex items-center px-3.5 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm transition-colors duration-150"
          >
            <svg class="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          <div>
            <h1 class="text-2xl font-black text-gray-900 leading-tight">Detalle de Cuota</h1>
            <p class="text-xs text-gray-500 font-semibold">Emitida el: {{ formatDate(order.date) }}</p>
          </div>
        </div>

        <div class="flex items-center space-x-2">
          <!-- Direct PDF Print Receipt link -->
          <a 
            :href="`/orders/${order.id}/report-storer-external`" 
            target="_blank"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Descargar Aviso (PDF)
          </a>
        </div>
      </div>

      <!-- Financial Status Indicators -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <!-- Total -->
        <div class="bg-indigo-600 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-indigo-100 uppercase tracking-wider">Total Facturado</dt>
                  <dd class="text-2xl font-black mt-1">${{ parseFloat(order.total).toFixed(2) }}</dd>
                  <dd class="text-xs text-indigo-200 mt-0.5">Bs. {{ (parseFloat(order.total) * change).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Paid -->
        <div class="bg-green-600 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-green-100 uppercase tracking-wider">Monto Abonado</dt>
                  <dd class="text-2xl font-black mt-1">${{ parseFloat(order.balance).toFixed(2) }}</dd>
                  <dd class="text-xs text-green-200 mt-0.5">Bs. {{ (parseFloat(order.balance) * change).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Outstanding -->
        <div class="bg-red-600 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-red-100 uppercase tracking-wider">Deuda Restante</dt>
                  <dd class="text-2xl font-black mt-1">${{ (order.total - order.balance).toFixed(2) }}</dd>
                  <dd class="text-xs text-red-200 mt-0.5">Bs. {{ ((order.total - order.balance) * change).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Layout Details Section -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        <!-- Left Column: Owner Meta & reported Payments -->
        <div class="lg:col-span-5 space-y-8">
          
          <!-- Owner info -->
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 class="text-md font-bold text-gray-900 mb-4 pb-2 border-b border-slate-100 flex items-center">
              <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Información de la Cuenta
            </h2>
            <div class="space-y-3">
              <div>
                <span class="block text-xs font-semibold text-gray-400 uppercase">Propietario</span>
                <span class="text-sm font-bold text-gray-900">{{ order.client.name }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-400 uppercase">Cédula o Alias</span>
                <span class="text-sm text-gray-900">{{ order.client.identification }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-400 uppercase">Inmueble / Apartado</span>
                <span class="text-sm text-gray-900">{{ order.client.companyName || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-400 uppercase">Tasa de Cambio Oficial</span>
                <span class="text-sm font-semibold text-indigo-600">{{ change }} Bs. / $</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-400 uppercase">Estado de la Cuota</span>
                <span class="mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider" :class="statusBadgeClass(order.order_state)">
                  <span v-if="order.order_state == 'cancel'">Anulada</span>
                  <span v-else-if="order.order_state == 'request'">Pendiente por Pago</span>
                  <span v-else-if="order.order_state == 'finish'">Cuota Cancelada</span>
                  <span v-else-if="order.order_state == 'pay'">Pagada</span>
                  <span v-else-if="order.order_state == 'delivery'">En espera de despacho</span>
                  <span v-else-if="order.order_state == 'payRevition'">Pago en revisión</span>
                  <span v-else>{{ order.order_state }}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Report Payment Form (Only if there is pending debt) -->
          <div v-if="order.total - order.balance > 0 && order.order_state !== 'cancel'" class="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 class="text-md font-bold text-gray-900 mb-2 flex items-center">
              <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Reportar Pago / Transferencia
            </h2>
            <p class="text-xs text-gray-500 mb-4 leading-relaxed">
              Registra el soporte de tu pago para esta cuota. El administrador revisará la transacción para su posterior aprobación.
            </p>

            <form @submit.prevent="submitPayReport">
              <div class="space-y-4">
                <!-- Payment method selection -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Método de Pago</label>
                  <select 
                    v-model="form.method" 
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    required
                  >
                    <option value="" disabled>Seleccione método...</option>
                    <option value="pago-movil">Pago Móvil Bs.</option>
                    <option value="efectivo">Efectivo Divisas ($)</option>
                    <option value="zelle">Zelle ($)</option>
                    <option value="transferencia">Transferencia Bancaria Bs.</option>
                  </select>
                </div>

                <!-- Pago Movil Bs -> USD calculation helper -->
                <div v-if="form.method === 'pago-movil' || form.method === 'transferencia'">
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Monto en Bolívares (Bs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="amountBs"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Monto en Bolívares"
                  />
                  <p class="text-xs text-indigo-500 mt-1">Equivale a: **${{ form.amount }} USD** (Tasa: {{ change }} Bs)</p>
                </div>

                <!-- USD amount input -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Monto a Reportar (USD $)</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="form.amount"
                    :disabled="form.method === 'pago-movil' || form.method === 'transferencia'"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white disabled:bg-slate-50"
                    placeholder="Ej: 20.00"
                    required
                  />
                </div>

                <!-- Payment Date -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Fecha del Pago</label>
                  <input
                    type="date"
                    v-model="form.payment_date"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    required
                  />
                </div>

                <!-- Reference number input -->
                <div v-if="form.method !== 'efectivo'">
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Número de Referencia</label>
                  <input
                    type="text"
                    v-model="form.reference"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Ej: Código del comprobante bancario"
                    required
                  />
                </div>

                <!-- Emisor details for Pago Móvil / Transferencia -->
                <div v-if="form.method === 'pago-movil' || form.method === 'transferencia'" class="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <span class="text-xs font-bold text-gray-700 uppercase tracking-wider block">Datos del Emisor (Conciliación Bancaria)</span>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-0.5">Teléfono Emisor</label>
                    <input
                      type="text"
                      v-model="form.issuer_phone"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                      placeholder="Ej: 04125394042"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-0.5">Cédula / RIF Emisor</label>
                    <input
                      type="text"
                      v-model="form.issuer_id"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                      placeholder="Ej: V-408616874"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-0.5">Banco de Origen</label>
                    <input
                      type="text"
                      v-model="form.issuer_bank"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                      placeholder="Ej: BNC (0191) / Mercantil"
                    />
                  </div>
                </div>

                <div class="pt-2">
                  <button
                    type="submit"
                    :disabled="form.processing"
                    class="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Reportar Pago Realizado
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>

        <!-- Right Column: Itemized billed products list and reported payments list -->
        <div class="lg:col-span-7 space-y-8">
          
          <!-- Billed expenses table -->
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 class="text-md font-bold text-gray-900 flex items-center">
                <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Conceptos Facturados en este Período
              </h2>
            </div>
            
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Concepto / Gasto</th>
                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Cant</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Precio Unitario</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Total ($)</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(p, key) in order.products" :key="key" class="hover:bg-slate-50/50 transition-colors">
                    <td class="px-6 py-4 text-sm text-gray-700 font-semibold">
                      {{ p.product?.name || 'Gasto de Condominio' }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 text-center">
                      {{ p.quantity }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 text-right">
                      ${{ parseFloat(p.price).toFixed(2) }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-900 font-bold text-right">
                      ${{ (p.price * p.quantity).toFixed(2) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-slate-50 font-bold text-gray-900 border-t border-slate-200">
                  <tr>
                    <td colspan="2" class="px-6 py-4 text-sm">Cálculo Total</td>
                    <td class="px-6 py-4 text-sm text-right">Total:</td>
                    <td class="px-6 py-4 text-sm text-right text-indigo-600 font-black">${{ parseFloat(order.total).toFixed(2) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Reported Payments list card -->
          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div class="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 class="text-md font-bold text-gray-900 flex items-center">
                <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Historial de Reportes y Pagos Recibidos
              </h2>
            </div>
            
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Método</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Monto ($)</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Referencia</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha Pago</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="pay in order.pays" :key="pay.id" class="hover:bg-slate-50/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold capitalize">
                      {{ pay.method }}
                      <div v-if="pay.issuer_bank || pay.issuer_phone || pay.issuer_id" class="text-xs text-gray-400 font-normal">
                        <span v-if="pay.issuer_bank">{{ pay.issuer_bank }} </span>
                        <span v-if="pay.issuer_phone">Tél: {{ pay.issuer_phone }} </span>
                        <span v-if="pay.issuer_id">CI: {{ pay.issuer_id }}</span>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold text-right">
                      ${{ parseFloat(pay.amount).toFixed(2) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                      {{ pay.reference || 'Abono general' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(pay.payment_date || pay.created_at) }}
                    </td>
                  </tr>
                  <tr v-if="!order.pays || order.pays.length === 0">
                    <td colspan="4" class="px-6 py-8 text-center text-sm text-gray-500">
                      No se han reportado abonos o comprobantes para esta alícuota de condominio.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'

export default {
  components: {
    Head,
    Link,
  },
  props: {
    order: Object,
    dispatches: Object,
    change: Number,
  },
  watch: {
    'form.method'(newVal) {
      this.form.reference = '';
      this.amountBs = '';
      this.form.amount = (this.order.total - this.order.balance).toFixed(2);
    },
    amountBs(newVal) {
      if (this.form.method === 'pago-movil' || this.form.method === 'transferencia') {
        const val = parseFloat(newVal);
        if (!isNaN(val) && val > 0) {
          this.form.amount = (val / this.change).toFixed(2);
        } else {
          this.form.amount = '0.00';
        }
      }
    }
  },
  data() {
    return {
      amountBs: '',
      form: this.$inertia.form({
        amount: (this.order.total - this.order.balance).toFixed(2),
        method: '',
        order_id: this.order.id,
        reference: '',
        payment_date: new Date().toISOString().substr(0, 10),
        issuer_bank: '',
        issuer_phone: '',
        issuer_id: '',
        issuer_name: ''
      })
    }
  },
  methods: {
    back() {
      // Return to public statement page of the owner
      window.location.href = `/payments/${this.order.client_id}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const cleanDate = dateString.replace('T', ' ').split(' ')[0];
        const parts = cleanDate.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    },
    statusBadgeClass(state) {
      switch (state) {
        case 'cancel':
          return 'bg-red-100 text-red-800';
        case 'request':
          return 'bg-yellow-100 text-yellow-800';
        case 'finish':
        case 'pay':
          return 'bg-green-100 text-green-800';
        case 'delivery':
          return 'bg-blue-100 text-blue-800';
        case 'payRevition':
          return 'bg-purple-100 text-purple-800';
        default:
          return 'bg-slate-100 text-slate-800';
      }
    },
    submitPayReport() {
      const pendingAmount = this.order.total - this.order.balance;
      const reportedAmount = parseFloat(this.form.amount);

      if (isNaN(reportedAmount) || reportedAmount <= 0) {
        alert('Por favor, introduzca un monto válido superior a cero.');
        return;
      }
      
      if (reportedAmount > pendingAmount + 0.01) {
        alert(`No se puede reportar un pago mayor a la deuda pendiente ($${pendingAmount.toFixed(2)} USD).`);
        return;
      }

      if (this.form.method !== 'efectivo' && !this.form.reference.trim()) {
        alert('Debe ingresar un número de referencia para este pago.');
        return;
      }

      const confirmMessage = `¿Confirmar reporte de pago por $${reportedAmount.toFixed(2)} USD?`;
      if (confirm(confirmMessage)) {
        this.form.post('/orders-external/pay', {
          onSuccess: () => {
            this.amountBs = '';
            this.form.reset();
          }
        });
      }
    }
  }
}
</script>
